import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum } from "../globalProps";
import { SectionShell } from "../SectionShell";

const statSchema = z.object({
  value: z.string().default("0"),
  label: z.string().default("Statistic"),
  prefix: z.string().optional(),
  suffix: z.string().optional(),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().optional(),
  stats: z
    .array(statSchema)
    .default([
      { value: "15", suffix: "+", label: "Years of Experience" },
      { value: "2,500", suffix: "+", label: "Clients Served" },
      { value: "98", suffix: "%", label: "Client Satisfaction" },
      { value: "50", suffix: "+", label: "Professional Team Members" },
    ]),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(4),
  backgroundStyle: backgroundStyleEnum.default("primary"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  const colClasses: Record<number, string> = {
    2: "grid-cols-1 @md:grid-cols-2",
    3: "grid-cols-1 @md:grid-cols-3",
    4: "grid-cols-2 @md:grid-cols-4",
  };

  if (safe.stats.length === 0) return null;

  return (
    <SectionShell globals={safe}>
        {safe.heading && (
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold text-center mb-12"
            data-prop="heading"
          />
        )}
        <div className={`grid ${colClasses[safe.columns]} gap-8 @md:gap-4 text-center`}>
          {safe.stats.map((stat, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="text-4xl @md:text-5xl font-bold mb-2">
                {stat.prefix && <span>{stat.prefix}</span>}
                <RichText
                  html={stat.value}
                  data-prop={`stats.${i}.value`}
                />
                {stat.suffix && <span>{stat.suffix}</span>}
              </div>
              <RichText
                as="div"
                html={stat.label}
                className="text-sm @md:text-base opacity-80"
                data-prop={`stats.${i}.label`}
              />
            </div>
          ))}
        </div>
    </SectionShell>
  );
}
